import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, FileText, ArrowRight, ScrollText, BookOpen, Sparkles } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CONSTITUTIONAL_SLUG = "wait-theory-2-constitutional-authority-edition-2026";

/**
 * Constitutional Authority Edition feature section for /wait-theory.
 * White executive editorial styling.
 */
export default function ConstitutionalAuthoritySection() {
  const [pub, setPub] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/scfr/publications/${CONSTITUTIONAL_SLUG}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && d?.publication) setPub(d.publication); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!pub) return null;

  return (
    <section
      className="relative bg-[#FBFAF7] border-y border-[#E5E7EB] py-20 overflow-hidden"
      data-testid="constitutional-authority-section"
    >
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[460px] h-[460px] rounded-full bg-[#C8A157]/[0.10] blur-3xl" />
        <div className="absolute -bottom-32 -left-20 w-[420px] h-[420px] rounded-full bg-[#1E3A8A]/[0.06] blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -16 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.55 }}
            viewport={{ once: true }}
          >
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#C8A157]/15 border border-[#C8A157]/40 text-[#7B5A1A] text-xs font-semibold tracking-widest uppercase mb-5">
              <Sparkles className="w-3.5 h-3.5" />
              Constitutional Authority Edition · 2026
            </span>
            <h2 className="zodiac-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F1E47] leading-[1.1] mb-4">
              {pub.title}
            </h2>
            {pub.subtitle && (
              <p className="text-lg sm:text-xl text-[#475569] mb-6">{pub.subtitle}</p>
            )}
            <div className="border-l-2 border-[#C8A157] pl-5 mb-7">
              <p className="text-[#475569] leading-relaxed text-base sm:text-lg">
                {pub.abstract}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3 mb-7 text-sm text-[#6B7280]">
              <span className="flex items-center gap-1.5">
                <ScrollText className="w-4 h-4 text-[#991B1B]" />
                {pub.author}
              </span>
              <span className="w-1 h-1 rounded-full bg-[#9CA3AF]" />
              <span>{pub.institution}</span>
              <span className="w-1 h-1 rounded-full bg-[#9CA3AF]" />
              <span>Vol. 1 · Issue 1 · 2026</span>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to={`/scfr/${pub.slug}`} data-testid="read-full-paper-cta">
                <button className="h-12 px-6 rounded-md bg-[#991B1B] hover:bg-[#7F1D1D] text-white font-semibold inline-flex items-center gap-2 shadow-[0_4px_14px_rgba(153,27,27,0.25)]">
                  <BookOpen className="w-4 h-4" />
                  Read the Full Paper
                  <ArrowRight className="w-4 h-4" />
                </button>
              </Link>
              {pub.pdf_url && (
                <a href={pub.pdf_url} download data-testid="download-constitutional-pdf">
                  <button className="h-12 px-6 rounded-md border border-[#0F1E47]/15 text-[#0F1E47] hover:bg-white inline-flex items-center gap-2">
                    <Download className="w-4 h-4" />
                    Download PDF
                  </button>
                </a>
              )}
            </div>
          </motion.div>

          {/* Editorial cover card */}
          <motion.div
            initial={{ opacity: 0, x: 16 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            className="hidden lg:block"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-[0_24px_80px_rgba(15,30,71,0.18)] ring-1 ring-[#0F1E47]/10">
              {pub.featured_image_url ? (
                <img
                  src={pub.featured_image_url}
                  alt={pub.title}
                  className="w-full h-[440px] object-cover"
                />
              ) : (
                <div className="w-full h-[440px] bg-gradient-to-br from-[#1E3A8A] to-[#0F1E47] flex items-center justify-center">
                  <FileText className="w-20 h-20 text-[#C8A157]/40" />
                </div>
              )}
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0F1E47]/95 to-transparent p-6">
                <div className="flex items-center gap-2 text-[#C8A157] text-xs uppercase tracking-widest mb-2 font-semibold">
                  <ScrollText className="w-4 h-4" />
                  SCFR-Journal · Flagship Research
                </div>
                <p className="text-white text-sm font-medium">
                  Society & Credential Fatigue Review
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
